import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Divider, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguage } from '../../utils/i18n';
import DashboardRevenue from './revenue/DashboardRevenue';
import DashboardFuelPage from './fuel/DashboardFuelPage';
import DashboardAsset from './asset/DashboardAsset';
import InformationBubbleDialog from '../../components/InformationBubbleDialog';

export default function DashboardHeader() {
  const { t } = useLanguage();
  const location = useLocation();

  const [tab, setTab] = useState(0);
  const [openInfo, setOpenInfo] = useState(false);
  const [userName, setUserName] = useState('');
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserName(parsedUser?.fullname || parsedUser?.name || '');
      }
    } catch (error) {
      console.error('Gagal melakukan parsing data user dari localStorage', error);
    }
  }, []);

  useEffect(() => {
    if (location.pathname.includes('dashboard-expense')) {
      setTab(1);
    } else if (location.pathname.includes('dashboard-asset')) {
      setTab(2);
    } else {
      setTab(0);
    }
  }, [location.pathname]);

  const handleChange = (_, newValue) => {
    setTab(newValue);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ bgcolor: 'background.paper', width: '100%', borderRadius: 1 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', textTransform: 'capitalize' }}>
            {t('general.welcomeBack', 'Welcome Back')}
            {userName ? `, ${userName}` : ''}
          </Typography>
          <IconButton onClick={() => setOpenInfo(true)} sx={{ bgcolor: '#e3f2fd', color: '#0374DA', borderRadius: 2 }}>
            <InfoOutlinedIcon />
          </IconButton>
        </Box>

        <Divider />

        <Tabs
          value={tab}
          onChange={handleChange}
          textColor="primary"
          indicatorColor="primary"
          variant="fullWidth"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 36,
              fontWeight: 400,
              fontSize: 20,
              color: 'text.secondary'
            },
            '& .Mui-selected': {
              color: 'text.primary',
              fontWeight: 500
            },
            '& .MuiTabs-indicator': {
              height: 4,
              backgroundColor: '#1A237E'
            }
          }}
        >
          <Tab label={t('general.revenue', 'Revenue')} />
          <Tab label={t('general.expense', 'Expense')} />
          <Tab label={t('general.asset', 'Asset')} />
        </Tabs>
      </Box>

      <Box sx={{ flexGrow: 1, width: '100%' }}>
        {tab === 0 && <DashboardRevenue />}
        {tab === 1 && <DashboardFuelPage />}
        {tab === 2 && <DashboardAsset />}
      </Box>

      <InformationBubbleDialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title={
          tab === 0
            ? t('answer.titleRevenue', 'Dashboard Revenue Guide')
            : tab === 1
              ? t('answer.titleFuel', 'Dashboard Fuel Guide')
              : t('answer.titleAsset', 'Dashboard Asset Guide')
        }
        messages={
          tab === 0
            ? [
                { type: 'q', text: t('question.revenue1', 'How often is the data updated in this dashboard?') },
                {
                  type: 'a',
                  text: t(
                    'answer.revenue1',
                    'The Vehicle Location and Top Geolocations data are updated dynamically every 30 seconds. Meanwhile, the Vehicle Ritase updates every 10 minutes.'
                  )
                },
                { type: 'q', text: t('question.revenue2', 'What does the Period Range affect?') },
                {
                  type: 'a',
                  text: t(
                    'answer.revenue2',
                    'The Period Range filters data for Achievement, Top Ritase Chart, and Worst Idle Unit Chart. Note that the "Vehicle Status" summary is always in real-time, so it ignores the period range.'
                  )
                },
                { type: 'q', text: t('question.revenue3', 'How does the Refresh Every feature work?') },
                {
                  type: 'a',
                  text: t(
                    'answer.revenue3',
                    'The auto-refresh feature is only active when your period is set to "Today" or "Last 24 Hours". It is automatically disabled for Custom dates or past weeks.'
                  )
                }
              ]
            : tab === 1
              ? [
                  { type: 'q', text: t('question.fuel1', 'How does the Fuel Dashboard work?') },
                  {
                    type: 'a',
                    text: t(
                      'answer.fuel1',
                      'You can easily switch between Internal Combustion Engine (ICE) and Electric Vehicle (EV) modes at the top.'
                    )
                  },
                  { type: 'q', text: t('question.fuel2', 'What is the difference between those modes?') },
                  {
                    type: 'a',
                    text: t(
                      'answer.fuel2',
                      'Switching to ICE displays Fuel Usage in Liters. Switching to EV adapts the dashboard to show Energy Usage in kWh.'
                    )
                  },
                  { type: 'q', text: t('question.fuel3', 'What insights can I get here?') },
                  {
                    type: 'a',
                    text: t(
                      'answer.fuel3',
                      'It calculates consumption per unit and illustrates the consumption trends over your selected period, helping you monitor efficiency.'
                    )
                  }
                ]
              : [
                  { type: 'q', text: t('question.asset1', 'What information is provided in the Asset Dashboard?') },
                  {
                    type: 'a',
                    text: t(
                      'answer.asset1',
                      'It gives you a complete summary of your assets, including the total number of vehicles, how many have their engines running, and how many are under maintenance.'
                    )
                  },
                  { type: 'q', text: t('question.asset2', 'Does it track driver performance?') },
                  {
                    type: 'a',
                    text: t(
                      'answer.asset2',
                      'Yes! It monitors active drivers, calculates their Ritase, and highlights the Top Drivers by Ritase. It also keeps track of driver license expiration statuses to remind you.'
                    )
                  },
                  { type: 'q', text: t('question.asset3', 'How often does this update?') },
                  {
                    type: 'a',
                    text: t(
                      'answer.asset3',
                      'The vehicle GPS statuses update dynamically. The operational data follows your selected period range, with auto-refresh available when set to "Today".'
                    )
                  }
                ]
        }
      />
    </Box>
  );
}
